import { useRef, useState } from 'react';
import { View } from 'react-native';
import { Button, Checkbox, HelperText, RadioButton, Text, TextInput } from 'react-native-paper';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { associateDriver } from '../api/apiService';

const schema = z.object({ identifier: z.string().regex(/^\d{9}$/), name: z.string().max(255) });
type Fields = z.infer<typeof schema>;

export function WorkCompanyForm({ onSuccess, expectedRelationshipId }: {
  onSuccess: () => void;
  expectedRelationshipId?: number | null;
}) {
  const { t } = useTranslation('workCompany');
  const [kind, setKind] = useState<'collective' | 'individual'>('collective');
  // Set when nif.pt could not confirm a new NIPC: the typed name is accepted
  // provisionally and corrected later by the backend queue.
  const [collectiveNameRequired, setCollectiveNameRequired] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const guard = useRef(false);
  const { control, handleSubmit, formState: { errors } } = useForm<Fields>({
    resolver: zodResolver(schema), defaultValues: { identifier: '', name: '' },
  });
  const nameShown = kind === 'individual' || collectiveNameRequired;
  const submit = handleSubmit(async ({ identifier, name }) => {
    if (guard.current) return;
    if ((nameShown && !name.trim()) || (expectedRelationshipId != null && !confirmed)) {
      setError(t('confirmRequired', 'Confirm the company change and provide the actual fiscal name when required.'));
      return;
    }
    guard.current = true;
    setBusy(true);
    setError(null);
    try {
      const response = await associateDriver({
        company_nipc: identifier, company_name: nameShown ? name.trim() : '',
        work_identity_kind: kind, payer: 'self',
        confirm_company_change: expectedRelationshipId != null && confirmed,
        ...(expectedRelationshipId != null ? { expected_relationship_id: expectedRelationshipId } : {}),
      });
      if (!response.ok && kind === 'collective' && response.data?.code === 'work_identity_name_required') {
        setCollectiveNameRequired(true);
        setError(t('collectiveNameRequired', 'We could not validate the company automatically. Enter the company name to continue; it will be checked later.'));
        return;
      }
      if (!response.ok) {
        const detail = response.data?.detail;
        setError(typeof detail === 'string' ? detail : t('retry', 'Unable to validate the work company. Retry or contact support.'));
        return;
      }
      onSuccess();
    } catch {
      setError(t('retry', 'Unable to validate the work company. Retry or contact support.'));
    } finally {
      guard.current = false;
      setBusy(false);
    }
  });
  return <View style={{ gap: 16 }}>
    <Text variant="titleLarge">{t('title', 'Identify your work company')}</Text>
    <Text>{t('explanation', 'This identifies the company in your work reports. It does not buy a license, assign ownership or change vehicle/GPS permissions.')}</Text>
    <Text>{t('selfPayDeclaration', 'This declaration selects personal funding. It does not purchase coverage; company funding requires an invitation or company approval.')}</Text>
    <RadioButton.Group value={kind} onValueChange={value => setKind(value as 'collective' | 'individual')}>
      <RadioButton.Item label={t('collective', 'Company (NIPC)')} value="collective" />
      <RadioButton.Item label={t('individual', 'Individual business (NIF)')} value="individual" />
    </RadioButton.Group>
    <Controller control={control} name="identifier" render={({ field: { value, onChange } }) =>
      <TextInput testID="work-company-identifier" label={kind === 'individual' ? 'NIF' : 'NIPC'} value={value}
        onChangeText={onChange} keyboardType="number-pad" maxLength={9} disabled={busy} error={!!errors.identifier} />
    } />
    {errors.identifier && <HelperText type="error">{t('invalidIdentifier', 'Enter a valid nine-digit fiscal identifier.')}</HelperText>}
    {nameShown && <Controller control={control} name="name" render={({ field: { value, onChange } }) =>
      <TextInput testID="work-company-name"
        label={kind === 'individual' ? t('fiscalName', 'Actual fiscal name') : t('companyName', 'Company name')}
        value={value} onChangeText={onChange} disabled={busy} />
    } />}
    {expectedRelationshipId != null && <Checkbox.Item status={confirmed ? 'checked' : 'unchecked'}
      label={t('confirmChange', 'I confirm changing my work company; existing work retains its original company.')}
      onPress={() => setConfirmed(value => !value)} disabled={busy} />}
    {error && <Text accessibilityRole="alert">{error}</Text>}
    <Button testID="work-company-submit" mode="contained" loading={busy} disabled={busy} onPress={submit}>
      {t('save', 'Confirm work company')}
    </Button>
  </View>;
}
